import theme from '../../theme';
import { useState } from 'react';
import ResourceBar from './ResourceBar';
import ResultScreen from './ResultScreen';
import PlannerDiagram from './PlannerDiagram';
import { GOODS, TOTAL, SLIDER_MAX, calculateUsed } from './goods';

// ── Shared styles ─────────────────────────────────────────────────────────────

const cardStyle = {
  maxWidth: '560px',
  margin: '0 auto',
  background: theme.bgCard,
  borderRadius: '4px',
  border: '1px solid rgba(184, 149, 90, 0.3)',
  padding: '1.5rem',
  color: theme.textPrimary,
};

const primaryBtnStyle = (enabled) => ({
  padding: '10px 24px',
  background: enabled ? theme.gold : '#1d2c47',
  color: enabled ? theme.bgDark : 'rgba(240, 237, 230, 0.25)',
  border: 'none',
  borderRadius: '4px',
  fontSize: '13px',
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontWeight: '600',
  letterSpacing: '0.02em',
});

const ResourceTable = () => (
  <div style={{ background: theme.bgDark, borderRadius: '4px', border: '1px solid rgba(184, 149, 90, 0.2)', padding: '12px 16px', marginBottom: '1.5rem' }}>
    <p style={{ fontSize: '12px', fontWeight: '500', color: theme.textMuted, margin: '0 0 8px' }}>
      Resource requirements per unit. Total pool: 100W · 100M · 100E
    </p>
    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ color: theme.textMuted }}>
          <td style={{ padding: '4px 0' }}>Good</td>
          <td style={{ textAlign: 'center' }}>Workers</td>
          <td style={{ textAlign: 'center' }}>Material</td>
          <td style={{ textAlign: 'center' }}>Energy</td>
        </tr>
      </thead>
      <tbody>
        {GOODS.map(g => (
          <tr key={g.name}>
            <td style={{ padding: '3px 0', fontWeight: '500', color: theme.gold }}>{g.name}</td>
            <td style={{ textAlign: 'center', color: theme.textPrimary }}>{g.w}</td>
            <td style={{ textAlign: 'center', color: theme.textPrimary }}>{g.m}</td>
            <td style={{ textAlign: 'center', color: theme.textPrimary }}>{g.e}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

function EconomicPlanner() {
  const [screen, setScreen] = useState('intro');
  const [qty, setQty] = useState([0, 0, 0, 0, 0]);

  const used = calculateUsed(qty);
  const total = qty.reduce((a, b) => a + b, 0);
  const canSubmit = total > 0;

  const handleSlider = (i, val) => {
    const proposed = Number(val);
    const testQty = [...qty];
    testQty[i] = proposed;
    const test = calculateUsed(testQty);
    if (test.w <= TOTAL && test.m <= TOTAL && test.e <= TOTAL) {
      setQty(testQty);
    }
  };

  const handleReset = () => {
    setQty([0, 0, 0, 0, 0]);
    setScreen('intro');
  };

  // ── Result screen ──────────────────────────────────────────────────────────
  if (screen === 'result') {
    return (
      <div style={cardStyle}>
        <ResultScreen qty={qty} total={total} onReset={handleReset} />
      </div>
    );
  }

  // ── Intro screen ───────────────────────────────────────────────────────────
  if (screen === 'intro') {
    return (
      <div style={cardStyle}>
        <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '0.25rem' }}>The Calculation Problem</p>
        <h2 style={{ fontSize: '20px', color: theme.gold, margin: '0 0 1rem', fontFamily: "'EB Garamond', Georgia, serif" }}>
          You are the Central Planner
        </h2>
        <PlannerDiagram />
        <p style={{ fontSize: '14px', color: theme.textPrimary, lineHeight: '1.6', marginBottom: '1rem' }}>
          You control the entire economy. Decide how many units of each good to produce using your limited resources.
          What you don't know: what citizens actually want.
        </p>
        <p style={{ fontSize: '14px', color: theme.textPrimary, lineHeight: '1.6', marginBottom: '1.5rem' }}>
          You have <strong>100 workers, 100 units of material, and 100 units of energy</strong>. Submit your plan,
          then find out how well you matched real demand.
        </p>
        <ResourceTable />
        <button onClick={() => setScreen('plan')} style={primaryBtnStyle(true)}>
          Start planning
        </button>
      </div>
    );
  }

  // ── Planning screen ────────────────────────────────────────────────────────
  return (
    <div style={cardStyle}>
      <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '0.25rem' }}>Step 1 of 2: Allocate resources</p>
      <h2 style={{ fontSize: '20px', color: theme.gold, margin: '0 0 1rem', fontFamily: "'EB Garamond', Georgia, serif" }}>
        Your production plan
      </h2>
      <PlannerDiagram />
      <ResourceBar workers={used.w} materials={used.m} energy={used.e} />

      {GOODS.map((g, i) => (
        <div key={g.name} style={{
          border: '1px solid rgba(184, 149, 90, 0.2)',
          borderRadius: '4px',
          padding: '12px 16px',
          marginBottom: '8px',
          background: theme.bgDark,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: theme.textPrimary }}>{g.name}</span>
            <span style={{ fontSize: '18px', fontWeight: '500', color: theme.gold }}>{qty[i]}</span>
          </div>
          <input
            type="range"
            min="0"
            max={SLIDER_MAX}
            step="1"
            value={qty[i]}
            onChange={e => handleSlider(i, e.target.value)}
            style={{ width: '100%', cursor: 'pointer' }}
            aria-label={`Allocate units for ${g.name}`}
          />
          <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>
            {g.w}W · {g.m}M · {g.e}E per unit
          </div>
        </div>
      ))}

      <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={() => setScreen('result')}
          disabled={!canSubmit}
          style={primaryBtnStyle(canSubmit)}
        >
          Submit plan
        </button>
        <span style={{ fontSize: '13px', color: theme.textMuted }}>Total units: {total}</span>
      </div>
    </div>
  );
}

export default EconomicPlanner;
